import axios from "axios";

// Use the correct API URL directly
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://affiliate-api.e-point.app";

// Types
export interface Program {
  _id: string;
  name: string;
  logo: string;
  affiliateUrl: string;
  cashbackRate: number;
  cashbackType?: "percent" | "fixed";
  cashbackFixedAmount?: number;
  visibility: boolean;
  countries: string[];
  descriptionCustomer?: string;
  conditions?: string;
  status?: string;
  isArchived?: boolean;
  archivedAt?: string;
  priority: number;
  // Legacy: single category/subcategory (backward compatibility)
  category?: string | { _id: string; name: string; categoryName?: string };
  subcategory?: string | { _id: string; name: string };
  // New: multiple categories/subcategories support
  categories?: string[];
  subcategories?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProgramData {
  descriptionCustomer?: string;
  conditions?: string;
  cashbackRate?: number;
  cashbackType?: "percent" | "fixed";
  cashbackFixedAmount?: number;
  countries?: string[];
  priority?: number;
  // Legacy: single category/subcategory (backward compatibility)
  category?: string;
  subcategory?: string;
  // New: multiple categories/subcategories support
  categories?: string[];
  subcategories?: string[];
}

// Category Types (from BPE-BE)
export interface SystemCategory {
  _id: string;
  categoryName: string;
  subcategories: Array<string | { _id: string; name: string }>;
  subcategoriesWithImages?: Array<{
    name: string;
    imageUrl?: string;
  }>;
  imageUrl?: string;
  displayOrder: number;
  isActive: boolean;
  icon?: string;
  description?: string;
}

export interface SyncCredentials {
  publisherId: string;
  accessToken: string;
  countryCode: string;
  includeHidden: boolean;
  relationship: string;
}

export interface SyncResults {
  created: number;
  updated: number;
  total: number;
}

export interface ProgramStats {
  total: number;
  active: number;
  visible: number;
  hidden: number;
  archived: number;
}

export interface Slider {
  _id: string;
  imageUrl: string;
  imageUrlCropped?: string;
  linkType: "internal" | "external" | "online";
  linkValue: string;
  countries: string[];
  language: string;
  displayOrder: number;
  isActive: boolean;
  title?: string;
  description?: string;
  buttonText?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OnlineShop {
  storeId: string;
  storeName: string;
  affiliateUrl: string;
  logoImage?: string;
  cashbackRate?: number;
  countries?: string[];
  storeType?: string;
}

export interface CreateSliderData {
  imageUrl: string;
  imageUrlCropped?: string;
  linkType: "internal" | "external" | "online";
  linkValue: string;
  countries: string[];
  language: string;
  displayOrder?: number;
  isActive?: boolean;
  title?: string;
  description?: string;
  buttonText?: string;
}

export interface UpdateSliderData {
  imageUrl?: string;
  imageUrlCropped?: string;
  linkType?: "internal" | "external" | "online";
  linkValue?: string;
  countries?: string[];
  language?: string;
  displayOrder?: number;
  isActive?: boolean;
  title?: string;
  description?: string;
  buttonText?: string;
}

export interface FlyerTemplateQrPlaceholder {
  x: number;
  y: number;
  width: number;
  height: number;
  safeMargin: number;
}

export interface FlyerTemplatePage {
  imageUrl: string;
  width?: number;
  height?: number;
  qrPlaceholder?: FlyerTemplateQrPlaceholder;
  placeholderSource?: "marker" | "blank" | "default";
}

export interface FlyerTemplate {
  _id: string;
  title: string;
  description?: string;
  category: string;
  language: string;
  countries: string[];
  tags: string[];
  // imageUrl/width/height/qrPlaceholder mirror pages[0] for back-compat.
  imageUrl: string;
  sourceType: "image" | "pdf";
  pdfUrl?: string;
  width?: number;
  height?: number;
  qrPlaceholder?: FlyerTemplateQrPlaceholder;
  hasQr: boolean;
  // Empty on rows created before multi-page support: synthesize page 1
  // from the flat mirrors above.
  pages: FlyerTemplatePage[];
  isActive: boolean;
  isFeatured: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFlyerTemplateData {
  title: string;
  description?: string;
  category: string;
  language: string;
  countries: string[];
  tags: string[];
  imageUrl: string;
  sourceType: "image" | "pdf";
  pdfUrl?: string;
  width?: number;
  height?: number;
  qrPlaceholder?: FlyerTemplateQrPlaceholder;
  hasQr: boolean;
  pages: FlyerTemplatePage[];
  isActive: boolean;
  isFeatured: boolean;
  displayOrder: number;
}

export type UpdateFlyerTemplateData = Partial<CreateFlyerTemplateData>;

export interface UploadFlyerTemplateResult {
  imageUrl: string;
  pdfUrl?: string;
  sourceType: "image" | "pdf";
  width?: number;
  height?: number;
  suggestedPlaceholder?: FlyerTemplateQrPlaceholder;
  placeholderSource?: "marker" | "blank" | "default";
  // suggestedPlaceholder is absent on pages 2+ unless a marker was found there.
  pages: {
    imageUrl: string;
    width?: number;
    height?: number;
    suggestedPlaceholder?: FlyerTemplateQrPlaceholder;
    placeholderSource?: "marker" | "blank" | "default";
  }[];
  pageCount: number;
}

export interface Country {
  id: string;
  name: string;
  currency: string;
  code: string | null;
  isEuro: boolean;
  isActive: boolean;
  displayOrder: number;
}

export interface CreateCountryData {
  name: string;
  currency: string;
  // null (not undefined/omitted) explicitly clears the code on update — see
  // CountryFormModal, DEVQA-13.
  code?: string | null;
  displayOrder?: number;
  isActive?: boolean;
}

export type UpdateCountryData = Partial<CreateCountryData>;

export interface Store {
  storeId: string;
  storeName: string;
  street?: string;
  housenumber?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  phonePrefix?: string;
  phoneNumber?: string;
  websiteUrl?: string;
  storeCategory?: string | { _id: string; name: string; categoryName?: string };
  storeSubcategory?: string | { _id: string; name: string };
  coverImage?: string;
  logoImage?: string;
  descriptionText?: string;
  cashbackRate?: number;
  specialOfferCashbackRate?: number;
  latitude?: number;
  longitude?: number;
  distance?: number;
  dealerId: string;
  companyName?: string;
  vatId?: string;
  status?: string;
  storeType?: string;
  businessHours?: {
    regularHours: Array<{
      dayOfWeek: number;
      isOpen: boolean;
      openTime?: string;
      closeTime?: string;
      breakHours?: Array<{
        startTime: string;
        endTime: string;
      }>;
    }>;
    timezone?: string;
    isVisible?: boolean;
  };
}

export interface Dealer {
  _id: string;
  dealerId: string;
  email: string;
  companyName?: string;
  country?: string;
  phonePrefix?: string;
  phoneNumber?: string;
  currency?: string;
  vatId?: string;
  vatValidity?: boolean;
  status?: string;
  sponsorId?: string;
  sponsorType?: string;
  dealerIdSalesBackoffice?: string;
  dealerAgentIdBackoffice?: string;
  qrLink?: string;
  latitude?: number;
  longitude?: number;
  activationStatus?: string;
  activationDate?: string;
  activationMethod?: string;
  activationVoucherCode?: string;
  activationPaymentId?: string;
  activationInvoiceId?: string;
  totalTransactions?: number;
  totalSponsoredCustomers?: number;
  metricsLastUpdated?: string;
  contactPerson?: {
    salutation?: string;
    firstName?: string;
    lastName?: string;
    phonePrefix?: string;
    phoneNumber?: string;
    email?: string;
  };
  companyAddress?: {
    vatId?: string;
    street?: string;
    housenumber?: string;
    addressAddition?: string;
    zipCode?: string;
    city?: string;
    country?: string;
  };
  storeInformation?: {
    storeId?: string;
    storeName?: string;
    street?: string;
    housenumber?: string;
    addressAddition?: string;
    zipCode?: string;
    city?: string;
    country?: string;
    websiteUrl?: string;
    phonePrefix?: string;
    phoneNumber?: string;
    storeCategory?:
      | string
      | { _id: string; name: string; categoryName?: string };
    storeSubcategory?: string | { _id: string; name: string };
    descriptionText?: string;
    cashbackRate?: number;
    vatId?: string;
    latitude?: number;
    longitude?: number;
  };
  storeContact?: {
    phonePrefix?: string;
    phoneNumber?: string;
    websiteUrl?: string;
    confirmationType?: string;
  };
  stores?: Store[];
  cashbackSettings?: {
    dealerCashbackRate?: number;
    customerCashbackRate?: number;
    confirmationType?: string;
  };
  storeCategory?: string | { _id: string; name: string; categoryName?: string };
  storeSubcategory?: string | { _id: string; name: string };
  createdAt?: string;
  updatedAt?: string;
  totalStores?: number;
  assignedUrl?: string;
  marketingSetId?: string;
  redirects?: {
    assignedUrl: string;
    marketingSetId?: string;
    description?: string;
    isActive?: boolean;
  }[];
  createdByAutomation?: boolean;
}

export interface Customer {
  _id: string;
  customerId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phonePrefix?: string;
  phoneNumber?: string;
  country?: string;
  zipCode?: string;
  currency?: string;
  status?: string;
  sponsorId?: string;
  sponsorType?: string;
  referralCustomerId?: string;
  customerIdSalesBackoffice?: string;
  street?: string;
  houseNumber?: string;
  addressAddition?: string;
  city?: string;
  bankName?: string;
  iban?: string;
  bic?: string;
  privacyCheck?: boolean;
  privacyAcceptedAt?: string;
  communicationConsent?: boolean;
  createdAt?: string;
  updatedAt?: string;
  familyBadge?: FamilyBadge;
}

// Payout Types
export type PayoutStatus = "open" | "in_progress" | "complete" | "rejected";

export interface CustomerSnapshot {
  customerId: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phonePrefix?: string;
  phoneNumber?: string;
  street?: string;
  houseNumber?: string;
  addressAddition?: string;
  zipCode?: string;
  city?: string;
  country?: string;
}

export interface PayoutBankDetails {
  accountHolder: string;
  iban: string;
  bic: string;
  bankName?: string;
}

export interface Payout {
  _id: string;
  payoutId: string;
  customerId: string;
  customerDisplayId?: string;
  customerSnapshot: CustomerSnapshot;
  pointsAmount: number;
  euroAmount?: number;
  amount?: number;
  bankDetails?: PayoutBankDetails;
  // Legacy fields for backwards compatibility
  bankName?: string;
  iban?: string;
  bic?: string;
  status: PayoutStatus;
  statusHistory: Array<{
    status: PayoutStatus;
    changedAt: Date;
    changedBy?: string;
    reason?: string;
  }>;
  adminNote?: string;
  exportBatchId?: string;
  exportedAt?: Date;
  processedAt?: Date;
  processedBy?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PayoutStats {
  total: number;
  open: number;
  inProgress: number;
  complete: number;
  rejected: number;
  totalEuroAmount: number;
  totalPointsAmount: number;
}

export interface PayoutFilters {
  status?: PayoutStatus;
  search?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  limit?: number;
  page?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface ExportHistoryItem {
  batchId: string;
  exportedAt: string;
  count: number;
  totalAmount: number;
}

// Monthly Report Types (ePoint -> BestPrime inter-company report:
// dealer activation fees + top-ups, net amounts, by payment date)
export interface MonthlyReportMeta {
  _id: string;
  year: number;
  month: number; // 1-12
  fileName: string;
  activationFeeCount: number;
  activationFeeNet: number;
  topUpCount: number;
  topUpNet: number;
  totalCount: number;
  totalNet: number;
  voucherActivationCount?: number; // absent on reports generated before the field existed
  generatedAt: string;
  generatedBy?: string;
  generationCount: number;
}

export interface GenerateMonthlyReportResult {
  report: MonthlyReportMeta;
  fileName: string;
  pdfBase64: string;
}

// Dealer Commission Payout Types
export type CommissionPayoutStatus =
  | "open"
  | "in_progress"
  | "complete"
  | "rejected";

export interface CommissionPayoutBankDetails {
  accountHolder: string;
  iban: string;
  bic: string;
}

export interface CommissionDealerSnapshot {
  companyName?: string;
  firstName?: string;
  lastName?: string;
  street?: string;
  housenumber?: string;
  addressAddition?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  vatId?: string;
  email?: string;
}

export interface CommissionPayout {
  _id: string;
  commissionPayoutId: string;
  dealerId: string;
  netAmount: number;
  vatPercent: number;
  vatAmount: number;
  grossAmount: number;
  currency: string;
  specialText?: string;
  status: CommissionPayoutStatus;
  bankDetails?: CommissionPayoutBankDetails;
  dealerSnapshot: CommissionDealerSnapshot;
  exportBatchId?: string;
  exportedAt?: string;
  processedBy?: string;
  processedAt?: string;
  rejectionReason?: string;
  adminNotes?: string;
  transactionEngineRef?: string;
  transactionEngineStatus?: string;
  creditNoteId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommissionStats {
  total: number;
  open: number;
  inProgress: number;
  complete: number;
  rejected: number;
  totalAmount: number;
  totalPaidAmount: number;
}

export interface CommissionFilters {
  status?: CommissionPayoutStatus;
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  page?: number;
}

// Energy Saving Types
export type EnergySavingStatus =
  | "open"
  | "in_progress"
  | "complete"
  | "rejected";

export interface EnergySaving {
  _id: string;
  energySavingId: string;
  customerId: string;
  customerSnapshot: CustomerSnapshot;
  pointsAmount: number;
  amount?: number; // Backend field name
  euroAmount?: number; // Legacy/alias - may not exist in DB
  energyContractNumber: string;
  energyContractPartner: string;
  status: EnergySavingStatus;
  statusHistory: Array<{
    status: EnergySavingStatus;
    changedAt: Date;
    changedBy?: string;
    reason?: string;
  }>;
  adminNote?: string;
  exportBatchId?: string;
  exportedAt?: Date;
  processedAt?: Date;
  processedBy?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EnergySavingStats {
  total: number;
  open: number;
  inProgress: number;
  complete: number;
  rejected: number;
  totalEuroAmount: number;
  totalPointsAmount: number;
  byPartner: Array<{
    partner: string;
    count: number;
    totalAmount: number;
  }>;
}

export interface EnergySavingFilters {
  status?: EnergySavingStatus;
  search?: string;
  energyPartner?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  limit?: number;
  page?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

// Consultant Request Types
export type ConsultantRequestStatus =
  | "pending"
  | "contacted"
  | "converted"
  | "rejected";

export interface ConsultantRequest {
  _id: string;
  requestId: string;
  customerId: string;
  customerDisplayId: string;
  contactEmail: string;
  contactPhone: string;
  comment: string;
  status: ConsultantRequestStatus;
  customerSnapshot: {
    firstName?: string;
    lastName?: string;
    email: string;
    phonePrefix?: string;
    phoneNumber?: string;
    country?: string;
    currentPoints?: number;
  };
  backofficeLeadId?: string;
  backofficeResponse?: {
    success: boolean;
    message?: string;
    leadId?: string;
    sentAt?: Date;
  };
  adminNotes?: string;
  processedAt?: Date;
  processedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConsultantRequestFilters {
  status?: ConsultantRequestStatus;
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  page?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface ConsultantRequestStats {
  total: number;
  pending: number;
  contacted: number;
  converted: number;
  rejected: number;
}

// Amazon OCR Claim Types
export type AmazonClaimStatus =
  | "pending"
  | "under_review"
  | "approved"
  | "rejected"
  | "paid";

export interface AmazonClaim {
  _id: string;
  customerId: string;
  customerDisplayId: string;
  screenshotUrl: string;
  screenshotKey?: string;
  screenshotHash?: string;
  ocrAmount: number;
  ocrCurrency: string;
  ocrConfidence: number;
  ocrProvider: "openai" | "manual";
  ocrRawResponse?: any;
  amazonPeriod?: string;
  amazonPeriodStart?: string;
  amazonPeriodEnd?: string;
  isAuthentic?: boolean;
  fraudScore?: number;
  fraudReasons?: string[];
  customerAmount?: number;
  customerCurrency?: string;
  customerEdited?: boolean;
  customerNote?: string;
  finalAmount?: number;
  finalCurrency?: string;
  status: AmazonClaimStatus;
  reviewedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
  adminNote?: string;
  approvedAmount?: number;
  approvedCurrency?: string;
  commissionAmount?: number;
  estimatedRewardAmount?: number;
  estimatedRewardCurrency?: string;
  appliedCashbackPercent?: number;
  isDuplicate?: boolean;
  duplicateOfClaimId?: string;
  duplicateReason?: string;
  payoutId?: string;
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AmazonClaimFilters {
  status?: AmazonClaimStatus;
  customerId?: string;
  customerDisplayId?: string;
  ocrProvider?: "openai" | "manual";
  dateFrom?: string;
  dateTo?: string;
  minAmount?: number;
  maxAmount?: number;
  isDuplicate?: boolean;
  minConfidence?: number;
  maxConfidence?: number;
  amazonPeriod?: string;
  skip?: number;
  limit?: number;
  sortBy?: "createdAt" | "ocrAmount" | "ocrConfidence" | "status";
  sortOrder?: "asc" | "desc";
}

export interface AmazonClaimStats {
  total: number;
  pending: number;
  underReview: number;
  approved: number;
  rejected: number;
  paid: number;
  totalApprovedAmount: number;
  totalPaidAmount: number;
  duplicateCount: number;
  averageConfidence: number;
  suspiciousCount: number;
  averageFraudScore: number;
}

export interface AmazonConfig {
  _id: string;
  cashbackPercent: number;
  createdAt: string;
  updatedAt: string;
}

// Refer-a-Friend admin report (read-only)
export interface AdminReferralReferrer {
  customerId?: string;
  firstName?: string;
  lastName?: string;
  referralCode?: string;
}

export interface AdminReferralRow {
  customerId: string;
  firstName?: string;
  lastName?: string;
  country?: string;
  createdAt: string;
  referrer?: AdminReferralReferrer;
}

export interface AdminReferralList {
  referrals: AdminReferralRow[];
  total: number;
  limit: number;
  skip: number;
}

export interface AdminReferralStats {
  totalReferred: number;
  uniqueReferrers: number;
  topReferrers: Array<AdminReferralReferrer & { referredCount: number }>;
}

export interface AppSettings {
  dealerTutorialEnabled: boolean;
  // Refer-a-Friend (optional: older backends may not return them yet)
  referralEnabled?: boolean;
  // Fraction, e.g. 0.01 = 1% — the UI renders it as a percentage.
  referralRate?: number;
  // Family Account (optional: older backends may not return them yet).
  // Defaults when absent: false, true, 6 (2–6, incl. the admin), 15, 10, [].
  familyAccountEnabled?: boolean;
  familyMemberLoginEnabled?: boolean;
  familyMaxMembers?: number;
  familyInviteTtlDays?: number;
  familyInviteDailyLimit?: number;
  // Pilot owners' customer ids. Replaced as a whole on save: never send []
  // unless the admin edited the list.
  familyAllowlistCustomerIds?: string[];
}

// Family Account support tools (affiliate-manager-be proxies epoint-be's
// /admin family routes 1:1; bodies keep epoint-be's { data } envelope).
export type FamilyMemberStatus =
  | "pending"
  | "active"
  | "expired"
  | "cancelled"
  | "removed"
  | "left";

// Set on a customer search hit that was found through a family member's
// e-mail: the row is the family owner, the badge describes the member.
export interface FamilyBadge {
  role: "member";
  status: string;
  familyId: string;
}

export interface FamilyAccountSearchItem {
  familyId: string;
  ownerCustomerId: string;
  ownerName: string | null;
  // Owner + active + pending.
  seatsUsed: number;
  active: number;
  pending: number;
  hasGoal: boolean;
  createdAt: string | null;
  dissolvedAt: string | null;
}

export interface FamilyAccountSearchResult {
  items: FamilyAccountSearchItem[];
  total: number;
  limit: number;
  skip: number;
}

export interface FamilyAccountSearchParams {
  search?: string;
  limit?: number;
  skip?: number;
}

export interface FamilyAccountAdminSummary {
  familyId: string;
  ownerCustomerId: string;
  seatsUsed: number;
  maxSeats: number;
  enabled: boolean;
  ownerLang?: string | null;
  createdAt: string | null;
  dissolvedAt: string | null;
  dissolvedReason: string | null;
}

export interface FamilyOwnerAdminSummary {
  customerId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

export interface FamilyMemberAdminRow {
  memberId: string;
  status: FamilyMemberStatus;
  firstName: string | null;
  lastName: string | null;
  // Plain address for pending / active rows only; null once terminal.
  email: string | null;
  emailMasked: string | null;
  invitedAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  endedAt: string | null;
  cancelReason: string | null;
  removedBy: string | null;
  removedAt: string | null;
  inviteSendCount: number;
  lastInviteSentAt: string | null;
  lastLoginAt: string | null;
  lastLoginCountry: string | null;
  hasPassword: boolean;
  // GDPR erasure (C1): the row stays for audit without personal data.
  erased: boolean;
  erasedAt: string | null;
  erasedBy: string | null;
}

export interface FamilyGoalAdminView {
  goalId: string;
  title: string;
  // Short-lived signed URL: refetch the view once imageUrlExpiresAt passed.
  imageUrl: string | null;
  imageUrlExpiresAt: string | null;
  targetEp: number;
  reachedAt: string | null;
  celebrationSeenCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface FamilyGoalHistoryEntry {
  goalId: string;
  title: string;
  targetEp: number;
  createdAt: string;
  closedAt: string;
  reachedAt: string | null;
  outcome: "reached" | "deleted" | "replaced" | string;
}

export interface FamilyEvent {
  eventId: string;
  type: string;
  actorType: "owner" | "member" | "support" | "system" | string;
  actorId: string;
  targetMemberId: string | null;
  reason: string | null;
  meta: Record<string, unknown>;
  createdAt: string;
}

export interface CustomerFamilyAccountView {
  // null when the customer exists but has no family.
  family: FamilyAccountAdminSummary | null;
  owner: FamilyOwnerAdminSummary | null;
  members: FamilyMemberAdminRow[];
  goal: FamilyGoalAdminView | null;
  goalHistory: FamilyGoalHistoryEntry[];
  events: FamilyEvent[];
}

export interface RemoveFamilyMemberResult {
  removed: true;
  seats: { used: number; max: number };
}

export interface CancelFamilyInvitationResult {
  cancelled: true;
}

export interface DissolveFamilyResult {
  dissolved: true;
  removed: number;
  cancelled: number;
  goalDeleted: boolean;
}

export interface ResetFamilyMemberCredentialResult {
  reset: true;
  // false: sessions ended, but the notice mail could not be sent.
  mailed: boolean;
}

export interface RemoveFamilyGoalImageResult {
  // false when the goal had no image.
  removed: boolean;
}

export interface EraseFamilyMemberResult {
  memberId: string;
  erasedAt: string;
}

interface FamilyApiEnvelope<T> {
  data: T;
  statusCode?: number;
  message?: string;
}

// Dissolve revokes every member session and sends mails; affiliate-manager-be
// allows epoint-be 30 s, so the browser waits a little longer than that.
export const FAMILY_DISSOLVE_TIMEOUT_MS = 45000;

const familyPath = (familyId: string) =>
  `/admin/family-accounts/${encodeURIComponent(familyId)}`;
const familyMemberPath = (familyId: string, memberId: string) =>
  `${familyPath(familyId)}/members/${encodeURIComponent(memberId)}`;

// Energy Bill Country Config
export interface EnergyBillConfig {
  country: string;
  countryName?: string;
  enabled: boolean;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// API instance (affiliate-manager-be)
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  // Default budget for regular CRUD calls; long-running calls (uploads,
  // syncs, bulk jobs) pass their own explicit timeout per request.
  timeout: 30000,
});

// BPE-BE API instance (for stores, dealers, customers)
const BPE_BE_URL = process.env.REACT_APP_BPE_BE_URL || "http://localhost:5000";
const bpeApi = axios.create({
  baseURL: BPE_BE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 120000,
});

// Attaches the stored session token without exposing it to application logs.
bpeApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn("BPE API request has no authentication token.");
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// BPE API response interceptor
bpeApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error("BPE API request was rejected as unauthorized.");

      localStorage.removeItem("authToken");
      if (
        window.location.pathname !== "/login" &&
        window.location.pathname !== "/register"
      ) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// Attaches the stored session token without exposing it to application logs.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn("API request has no authentication token.");
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("authToken");
      if (
        window.location.pathname !== "/login" &&
        window.location.pathname !== "/register"
      ) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// Backfill Types

export interface FieldChange {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
  willUpdate: boolean;
}

export interface BackfillPreviewItem {
  type: "customer" | "dealer";
  id: string;
  idSalesBackoffice: string | null;
  backofficeUUID_csv: string;
  fieldChanges: FieldChange[];
}

export interface BackfillPreviewResult {
  customers: BackfillPreviewItem[];
  dealers: BackfillPreviewItem[];
  summary: {
    customerToUpdate: number;
    customerSkipped: number;
    customerNotFound: number;
    dealerToUpdate: number;
    dealerSkipped: number;
    dealerNotFound: number;
    totalParsedRows: number;
  };
}

export interface BackfillExecuteResult {
  summary: {
    customerUpdated: number;
    customerSkipped: number;
    customerNotFound: number;
    dealerUpdated: number;
    dealerSkipped: number;
    dealerNotFound: number;
  };
}

export interface BackofficeSyncFieldDiff {
  label: string;
  csvValue: string;
  bpeValue: string;
  willChange: boolean;
}

export interface BackofficeSyncPreviewItem {
  type: "customer" | "dealer";
  id: string;
  backofficeUUID: string;
  fieldDiffs: BackofficeSyncFieldDiff[];
}

export interface BackofficeSyncPreviewResult {
  customers: BackofficeSyncPreviewItem[];
  dealers: BackofficeSyncPreviewItem[];
  summary: {
    customerCount: number;
    dealerCount: number;
    customerFieldChanges: number;
    dealerFieldChanges: number;
  };
}

export interface BackofficeSyncExecuteResult {
  started: boolean;
  message: string;
}

export interface DealerGoalBackfillResult {
  checked: number;
  qualified: string[];
}

// Missing Records Recovery Types

export interface MissingRecordPreviewItem {
  type: "customer" | "dealer";
  csvId: string;
  csvEmail: string;
  csvName: string;
  csvBackofficeUUID: string;
  /** Raw CSV row data — used by the "Create One" button */
  csvRow: {
    firstName: string;
    lastName: string;
    companyName: string;
    phone: string;
    street: string;
    zipCode: string;
    city: string;
    country: string;
    sponsorId: string;
  };
  dbRecord: {
    id: string;
    email: string;
    name: string;
    backofficeStatus: string;
  } | null;
  status: "missing" | "exists";
}

export interface MissingRecordsPreviewResult {
  missing: MissingRecordPreviewItem[];
  existing: MissingRecordPreviewItem[];
  errors: { row: { type: string; id: string; email: string }; error: string }[];
  summary: {
    totalCsvRows: number;
    missingInEpoint: number;
    alreadyExist: number;
    parseErrors: number;
  };
}

export interface MissingRecordsExecuteResult {
  summary: {
    created: number;
    skipped: number;
    errors: number;
    total: number;
  };
  createdRecords: { type: string; id: string; email: string }[];
  errors: { id: string; error: string }[];
}

export const apiService = {
  // Programs
  getPrograms: async (visible?: boolean) => {
    const response = await api.get<Program[]>(
      `/affiliate-programs${visible ? "?visible=true" : ""}`,
    );
    return response.data;
  },

  getProgram: async (id: string) => {
    const response = await api.get<Program>(`/affiliate-programs/${id}`);
    return response.data;
  },

  updateProgram: async (id: string, data: UpdateProgramData) => {
    const response = await api.put<Program>(`/affiliate-programs/${id}`, data);
    return response.data;
  },

  toggleVisibility: async (id: string) => {
    const response = await api.put<Program>(
      `/affiliate-programs/${id}/toggle-visibility`,
    );
    return response.data;
  },

  deleteProgram: async (id: string) => {
    const response = await api.delete(`/affiliate-programs/${id}`);
    return response.data;
  },

  searchPrograms: async (name: string) => {
    const response = await api.get<Program[]>(
      `/affiliate-programs/search?name=${encodeURIComponent(name)}`,
    );
    return response.data;
  },

  getStats: async () => {
    const response = await api.get<ProgramStats>("/affiliate-programs/stats");
    return response.data;
  },

  getArchivedPrograms: async () => {
    const response = await api.get<Program[]>("/affiliate-programs/archived");
    return response.data;
  },

  // Sync
  syncPrograms: async (credentials: SyncCredentials) => {
    const response = await api.post<SyncResults>(
      "/affiliate-programs/sync",
      credentials,
      // Syncs against external affiliate networks; can run for minutes.
      { timeout: 300000 },
    );
    return response.data;
  },

  // Priority and Category Management
  getProgramsByPriority: async () => {
    const response = await api.get<Program[]>(
      "/affiliate-programs/by-priority",
    );
    return response.data;
  },

  updatePriority: async (id: string, priority: number) => {
    const response = await api.patch<Program>(
      `/affiliate-programs/${id}/priority`,
      { priority },
    );
    return response.data;
  },

  bulkUpdatePriorities: async (
    updates: Array<{ programId: string; priority: number }>,
  ) => {
    const response = await api.post<{ updated: number }>(
      "/affiliate-programs/priorities/bulk",
      { updates },
    );
    return response.data;
  },

  updateCategory: async (id: string, category: string) => {
    const response = await api.patch<Program>(
      `/affiliate-programs/${id}/category`,
      { category },
    );
    return response.data;
  },

  getCategories: async () => {
    const response = await api.get<string[]>(
      "/affiliate-programs/categories/list",
    );
    return response.data;
  },

  // Sliders
  getSliders: async () => {
    const response = await api.get<{
      data: { success: boolean; data: Slider[] };
    }>("/slider/admin");
    return response.data.data.data;
  },

  getSlider: async (id: string) => {
    const response = await api.get<{
      data: { success: boolean; data: Slider };
    }>(`/slider/admin/${id}`);
    return response.data.data.data;
  },

  createSlider: async (data: CreateSliderData) => {
    const response = await api.post<{
      data: { success: boolean; data: Slider };
    }>("/slider/admin", data);
    return response.data.data.data;
  },

  updateSlider: async (id: string, data: UpdateSliderData) => {
    const response = await api.put<{
      data: { success: boolean; data: Slider };
    }>(`/slider/admin/${id}`, data);
    return response.data.data.data;
  },

  toggleSlider: async (id: string) => {
    const response = await api.put<{
      data: { success: boolean; data: Slider };
    }>(`/slider/admin/${id}/toggle`);
    return response.data.data.data;
  },

  deleteSlider: async (id: string) => {
    const response = await api.delete<{ data: { success: boolean } }>(
      `/slider/admin/${id}`,
    );
    return response.data.data;
  },

  uploadSliderImage: async (
    file: File,
    language: string,
    cropWidth?: number,
    cropHeight?: number,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("language", language);
    if (cropWidth) formData.append("cropWidth", cropWidth.toString());
    if (cropHeight) formData.append("cropHeight", cropHeight.toString());

    const response = await api.post<{
      data: {
        success: boolean;
        data: { imageUrl: string; imageUrlCropped?: string };
      };
    }>("/slider/admin/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      // Multipart image upload + server-side crop can exceed the default.
      timeout: 120000,
    });
    return response.data.data.data;
  },

  // App Settings
  getAppSettings: async () => {
    const response = await api.get<{
      success: boolean;
      data: AppSettings;
    }>("/app-settings");
    return response.data.data;
  },

  updateAppSettings: async (data: Partial<AppSettings>) => {
    const response = await api.put<{
      success: boolean;
      data: AppSettings;
      message: string;
    }>("/app-settings/admin", data);
    return response.data.data;
  },

  // Flyer Templates
  getFlyerTemplates: async () => {
    const response = await api.get<{
      data: { success: boolean; data: FlyerTemplate[] };
    }>("/flyer-template/admin");
    return response.data.data.data;
  },

  getFlyerTemplate: async (id: string) => {
    const response = await api.get<{
      data: { success: boolean; data: FlyerTemplate };
    }>(`/flyer-template/admin/${id}`);
    return response.data.data.data;
  },

  getFlyerCategories: async () => {
    const response = await api.get<{
      data: { success: boolean; data: string[] };
    }>("/flyer-template/categories");
    return response.data.data.data;
  },

  createFlyerTemplate: async (data: CreateFlyerTemplateData) => {
    const response = await api.post<{
      data: { success: boolean; data: FlyerTemplate };
    }>("/flyer-template/admin", data);
    return response.data.data.data;
  },

  updateFlyerTemplate: async (id: string, data: UpdateFlyerTemplateData) => {
    const response = await api.put<{
      data: { success: boolean; data: FlyerTemplate };
    }>(`/flyer-template/admin/${id}`, data);
    return response.data.data.data;
  },

  toggleFlyerTemplate: async (id: string) => {
    const response = await api.put<{
      data: { success: boolean; data: FlyerTemplate };
    }>(`/flyer-template/admin/${id}/toggle`);
    return response.data.data.data;
  },

  toggleFeatureFlyerTemplate: async (id: string) => {
    const response = await api.put<{
      data: { success: boolean; data: FlyerTemplate };
    }>(`/flyer-template/admin/${id}/feature`);
    return response.data.data.data;
  },

  deleteFlyerTemplate: async (id: string) => {
    const response = await api.delete<{ data: { success: boolean } }>(
      `/flyer-template/admin/${id}`,
    );
    return response.data.data;
  },

  uploadFlyerTemplateFile: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<{
      data: { success: boolean; data: UploadFlyerTemplateResult };
    }>("/flyer-template/admin/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      // Large PDFs upload + rasterize server-side in ~25s.
      timeout: 120000,
    });
    return response.data.data.data;
  },

  // Countries (admin-managed; each maps to a currency for the app)
  getCountries: async () => {
    const response = await api.get<{
      data: { success: boolean; data: Country[] };
    }>("/countries/admin?includeInactive=true");
    return response.data.data.data;
  },

  createCountry: async (data: CreateCountryData) => {
    const response = await api.post<{
      data: { success: boolean; data: Country };
    }>("/countries/admin", data);
    return response.data.data.data;
  },

  updateCountry: async (id: string, data: UpdateCountryData) => {
    const response = await api.put<{
      data: { success: boolean; data: Country };
    }>(`/countries/admin/${id}`, data);
    return response.data.data.data;
  },

  toggleCountry: async (id: string) => {
    const response = await api.put<{
      data: { success: boolean; data: Country };
    }>(`/countries/admin/${id}/toggle`);
    return response.data.data.data;
  },

  deleteCountry: async (id: string) => {
    const response = await api.delete<{ data: { success: boolean } }>(
      `/countries/admin/${id}`,
    );
    return response.data.data;
  },

  // Stores
  searchStores: async (searchText: string) => {
    const response = await bpeApi.get<{
      data: {
        success: boolean;
        stores: Store[];
        pagination: {
          total: number;
          limit: number;
          offset: number;
          hasMore: boolean;
        };
      };
    }>("/customer/stores/search", {
      params: { searchText, limit: 20 },
    });
    return response.data.data.stores;
  },

  // Online Shops (Affiliate Partners)
  searchOnlineShops: async (searchText?: string) => {
    const response = await bpeApi.get<{
      data: {
        success: boolean;
        stores: OnlineShop[];
      };
    }>("/customer/stores/online", {
      params: searchText ? { search: searchText } : {},
    });
    return response.data.data.stores;
  },

  getOnlineShop: async (storeId: string) => {
    const response = await bpeApi.get<{
      data: {
        success: boolean;
        stores: OnlineShop[];
      };
    }>("/customer/stores/online");
    const shops = response.data.data.stores;
    return shops.find((shop) => shop.storeId === storeId) || null;
  },

  // Dealers (via affiliate-manager-be direct DB access)
  getDealers: async (
    params?: {
      limit?: number;
      skip?: number;
      search?: string;
      status?: string;
      marketingFilter?: "all" | "assigned" | "unassigned";
      imageFilter?: "all" | "missing_images";
      categoryFilter?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
      automationFilter?: "automation_only" | "non_automation";
    },
    options?: { timeout?: number },
  ) => {
    const response = await api.get<{
      success: boolean;
      data: Dealer[];
      total?: number;
    }>("/admin/dealers", {
      params,
      ...(options?.timeout ? { timeout: options.timeout } : {}),
    });
    return response.data; // Direct response from backend
  },

  updateDealerMetrics: async () => {
    const response = await api.post<{
      success: boolean;
      message: string;
    }>("/admin/dealers/metrics/update", undefined, {
      // Recomputes metrics across all dealers.
      timeout: 120000,
    });
    return response.data;
  },

  getDealer: async (id: string) => {
    const response = await api.get<{
      success: boolean;
      data: Dealer;
    }>(`/admin/dealers/${id}`);
    return response.data.data; // Direct response from backend
  },

  bulkCheckDealerStats: async (emails: string[]) => {
    const response = await bpeApi.post<{
      data: {
        success: boolean;
        data: Array<{
          email: string;
          companyName: string;
          status: string;
          dealerId: string;
          sponsoredCustomerCount: number;
          hasTransactions: string;
          transactionCount: number;
          storeCount: number;
          stores: Array<{
            storeId: string;
            storeName: string;
            status: string;
          }>;
        }>;
      };
    }>(
      "/admin/dealers/bulk-stats",
      { emails },
      { timeout: 300000 }, // 5 minutes timeout for this heavy bulk operation
    );
    return response.data.data;
  },

  bulkUpdateStoresStatus: async (
    dealerIds: string[],
    status: "active" | "pending" | "inactive",
  ) => {
    const response = await bpeApi.post<{
      success: boolean;
      updatedDealers: number;
    }>(
      "/admin/dealers/bulk-update-stores-status",
      { dealerIds, status },
      { timeout: 60000 }, // 60 seconds timeout for bulk update
    );
    return response.data;
  },

  getDealerStats: async () => {
    const response = await api.get<{
      total: number;
      active: number;
      inactive: number;
      pending: number;
    }>("/admin/dealers/stats");
    return response.data; // Direct response from backend
  },

  // Customers (via affiliate-manager-be direct DB access)
  getCustomers: async (params?: {
    limit?: number;
    skip?: number;
    search?: string;
    status?: string;
  }) => {
    const response = await api.get<{
      success: boolean;
      data: Customer[];
      total?: number;
    }>("/admin/customers", { params });
    return response.data; // Direct response from backend
  },

  getCustomer: async (id: string) => {
    const response = await api.get<{
      success: boolean;
      data: Customer;
    }>(`/admin/customers/${id}`);
    return response.data.data; // Direct response from backend
  },

  getCustomerStats: async () => {
    const response = await api.get<{
      total: number;
      active: number;
      inactive: number;
    }>("/admin/customers/stats");
    return response.data; // Direct response from backend
  },

  // Family Accounts (support tools via affiliate-manager-be). Every action
  // needs a reason (3–200 characters, no '@'); the backend validates again.
  searchFamilyAccounts: async (params: FamilyAccountSearchParams = {}) => {
    const response = await api.get<FamilyApiEnvelope<FamilyAccountSearchResult>>(
      "/admin/family-accounts",
      { params: { search: params.search, limit: params.limit, skip: params.skip } },
    );
    return response.data.data;
  },

  getCustomerFamilyAccount: async (customerId: string) => {
    const response = await api.get<FamilyApiEnvelope<CustomerFamilyAccountView>>(
      `/admin/customers/${encodeURIComponent(customerId)}/family-account`,
    );
    return response.data.data;
  },

  removeFamilyMember: async (
    familyId: string,
    memberId: string,
    reason: string,
  ) => {
    const response = await api.delete<FamilyApiEnvelope<RemoveFamilyMemberResult>>(
      familyMemberPath(familyId, memberId),
      { data: { reason } },
    );
    return response.data.data;
  },

  cancelFamilyInvitation: async (
    familyId: string,
    memberId: string,
    reason: string,
  ) => {
    const response = await api.delete<
      FamilyApiEnvelope<CancelFamilyInvitationResult>
    >(`${familyPath(familyId)}/invitations/${encodeURIComponent(memberId)}`, {
      data: { reason },
    });
    return response.data.data;
  },

  dissolveFamily: async (familyId: string, reason: string) => {
    const response = await api.post<FamilyApiEnvelope<DissolveFamilyResult>>(
      `${familyPath(familyId)}/dissolve`,
      { reason },
      { timeout: FAMILY_DISSOLVE_TIMEOUT_MS },
    );
    return response.data.data;
  },

  resetFamilyMemberCredential: async (
    familyId: string,
    memberId: string,
    reason: string,
  ) => {
    const response = await api.post<
      FamilyApiEnvelope<ResetFamilyMemberCredentialResult>
    >(`${familyMemberPath(familyId, memberId)}/reset-credential`, { reason });
    return response.data.data;
  },

  removeFamilyGoalImage: async (familyId: string, reason: string) => {
    const response = await api.delete<
      FamilyApiEnvelope<RemoveFamilyGoalImageResult>
    >(`${familyPath(familyId)}/goal/image`, { data: { reason } });
    return response.data.data;
  },

  eraseFamilyMember: async (
    familyId: string,
    memberId: string,
    reason: string,
  ) => {
    const response = await api.post<FamilyApiEnvelope<EraseFamilyMemberResult>>(
      `${familyMemberPath(familyId, memberId)}/erase`,
      { reason },
    );
    return response.data.data;
  },

  // Payouts
  getPayouts: async (params?: PayoutFilters) => {
    const response = await api.get<{
      statusCode: number;
      message: string;
      data: {
        payouts: Payout[];
        total: number;
        page: number;
        limit: number;
      };
    }>("/admin/payouts", { params });
    // Map backend response to expected format
    return {
      data: response.data.data.payouts,
      total: response.data.data.total,
      page: response.data.data.page,
      limit: response.data.data.limit,
    };
  },

  getPayoutStats: async () => {
    const response = await api.get<{ statusCode: number; data: PayoutStats }>(
      "/admin/payouts/stats",
    );
    return response.data.data;
  },

  bulkExportPayouts: async (payoutIds: string[]) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      batchId: string;
      count: number;
      totalAmount: number;
      excelBuffer: string; // base64 encoded
    }>("/admin/payouts/bulk/export", { payoutIds });
    return response.data;
  },

  bulkRejectPayouts: async (payoutIds: string[], rejectionReason: string) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      count: number;
    }>("/admin/payouts/bulk/reject", { payoutIds, rejectionReason });
    return response.data;
  },

  bulkMarkPayoutsPaid: async (payoutIds: string[]) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      count: number;
    }>("/admin/payouts/bulk/mark-paid", { payoutIds });
    return response.data;
  },

  getPayoutExportHistory: async (limit?: number) => {
    const response = await api.get<{
      statusCode: number;
      data: { batches: ExportHistoryItem[]; total: number };
    }>("/admin/payouts/export-history", { params: { limit } });
    return {
      data: response.data.data.batches,
      total: response.data.data.total,
    };
  },

  // Dealer Commission Payouts
  getCommissionPayouts: async (params?: CommissionFilters) => {
    const response = await api.get<{
      statusCode: number;
      message: string;
      data: {
        payouts: CommissionPayout[];
        total: number;
        page: number;
        limit: number;
      };
    }>("/admin/commissions", { params });
    return {
      data: response.data.data.payouts,
      total: response.data.data.total,
      page: response.data.data.page,
      limit: response.data.data.limit,
    };
  },

  getCommissionStats: async () => {
    const response = await api.get<{
      statusCode: number;
      data: CommissionStats;
    }>("/admin/commissions/stats");
    return response.data.data;
  },

  // Refer-a-Friend admin report (read-only)
  getAdminReferrals: async (params?: {
    limit?: number;
    skip?: number;
    search?: string;
  }) => {
    const response = await api.get<{
      statusCode: number;
      message: string;
      data: AdminReferralList;
    }>("/admin/referrals", { params });
    return response.data.data;
  },

  getAdminReferralStats: async () => {
    const response = await api.get<{
      statusCode: number;
      message: string;
      data: AdminReferralStats;
    }>("/admin/referrals/stats");
    return response.data.data;
  },

  bulkExportCommissions: async (commissionPayoutIds: string[]) => {
    const response = await api.post<{
      statusCode: number;
      message: string;
      batchId: string;
      count: number;
      totalAmount: number;
      excelBuffer: string; // base64 encoded
    }>("/admin/commissions/bulk/export", { commissionPayoutIds });
    return response.data;
  },

  bulkRejectCommissions: async (
    commissionPayoutIds: string[],
    rejectionReason: string,
  ) => {
    const response = await api.post<{
      statusCode: number;
      message: string;
      data: { updatedCount: number };
    }>("/admin/commissions/bulk/reject", {
      commissionPayoutIds,
      rejectionReason,
    });
    return response.data;
  },

  bulkMarkCommissionsPaid: async (commissionPayoutIds: string[]) => {
    const response = await api.post<{
      statusCode: number;
      message: string;
      data: { updatedCount: number };
    }>("/admin/commissions/bulk/mark-paid", { commissionPayoutIds });
    return response.data;
  },

  getCommissionExportHistory: async (limit?: number) => {
    const response = await api.get<{
      statusCode: number;
      data: { batches: ExportHistoryItem[]; total: number };
    }>("/admin/commissions/export-history", { params: { limit } });
    return {
      data: response.data.data.batches,
      total: response.data.data.total,
    };
  },

  // Monthly Reports (inter-company report PDF)
  getMonthlyReports: async () => {
    const response = await api.get<{
      statusCode: number;
      data: MonthlyReportMeta[];
    }>("/admin/monthly-reports");
    return response.data.data;
  },

  generateMonthlyReport: async (year: number, month: number) => {
    const response = await api.post<{
      statusCode: number;
      data: GenerateMonthlyReportResult;
    }>(
      "/admin/monthly-reports/generate",
      { year, month },
      // PDF generation + storage upstream can take a while on big months.
      { timeout: 120000 },
    );
    return response.data.data;
  },

  downloadMonthlyReport: async (id: string) => {
    const response = await api.get<{
      statusCode: number;
      data: { pdfBase64: string; fileName: string };
    }>(`/admin/monthly-reports/${id}/download`, { timeout: 60000 });
    return response.data.data;
  },

  // Energy Savings
  getEnergySavings: async (params?: EnergySavingFilters) => {
    const response = await api.get<{
      statusCode: number;
      message: string;
      data: {
        energySavings: EnergySaving[];
        total: number;
        page: number;
        limit: number;
      };
    }>("/admin/energy-savings", { params });
    // Map backend response to expected format
    return {
      data: response.data.data.energySavings,
      total: response.data.data.total,
      page: response.data.data.page,
      limit: response.data.data.limit,
    };
  },

  getEnergySavingStats: async () => {
    const response = await api.get<{
      statusCode: number;
      data: EnergySavingStats;
    }>("/admin/energy-savings/stats");
    return response.data.data;
  },

  getEnergyPartners: async () => {
    const response = await api.get<{
      statusCode: number;
      data: string[];
    }>("/admin/energy-savings/partners");
    return { data: response.data.data };
  },

  bulkExportEnergySavings: async (energySavingIds: string[]) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      batchId: string;
      count: number;
      totalAmount: number;
      excelBuffer: string; // base64 encoded
    }>("/admin/energy-savings/bulk/export", { energySavingIds });
    return response.data;
  },

  bulkRejectEnergySavings: async (
    energySavingIds: string[],
    reason: string,
  ) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      count: number;
    }>("/admin/energy-savings/bulk/reject", { energySavingIds, reason });
    return response.data;
  },

  bulkMarkEnergySavingsComplete: async (energySavingIds: string[]) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      count: number;
    }>("/admin/energy-savings/bulk/complete", { energySavingIds });
    return response.data;
  },

  getEnergySavingExportHistory: async (limit?: number) => {
    const response = await api.get<{
      statusCode: number;
      data: { batches: ExportHistoryItem[]; total: number };
    }>("/admin/energy-savings/export-history", { params: { limit } });
    return {
      data: response.data.data.batches,
      total: response.data.data.total,
    };
  },

  // System Categories (from BPE-BE)
  getSystemCategories: async () => {
    const response = await bpeApi.get<
      { data?: SystemCategory[] } | SystemCategory[]
    >("/categories");
    // Handle both direct array and wrapped response
    if (Array.isArray(response.data)) {
      return response.data;
    }
    // If wrapped in data property
    if (
      response.data &&
      "data" in response.data &&
      Array.isArray(response.data.data)
    ) {
      return response.data.data;
    }
    return [];
  },

  // Consultant Requests
  getConsultantRequests: async (params?: ConsultantRequestFilters) => {
    const response = await api.get<{
      statusCode: number;
      message: string;
      data: {
        requests: ConsultantRequest[];
        total: number;
        page: number;
        limit: number;
      };
    }>("/admin/consultant-requests", { params });
    return {
      data: response.data.data.requests,
      total: response.data.data.total,
      page: response.data.data.page,
      limit: response.data.data.limit,
    };
  },

  getConsultantRequestStats: async () => {
    const response = await api.get<{
      statusCode: number;
      data: ConsultantRequestStats;
    }>("/admin/consultant-requests/stats");
    return response.data.data;
  },

  updateConsultantRequestStatus: async (
    requestId: string,
    status: ConsultantRequestStatus,
    adminNotes?: string,
  ) => {
    const response = await api.put<{
      statusCode: number;
      message: string;
      data: ConsultantRequest;
    }>(`/admin/consultant-requests/${requestId}/status`, {
      status,
      adminNotes,
    });
    return response.data;
  },

  convertConsultantRequest: async (
    requestId: string,
    contractData: {
      energyContractNumber: string;
      energyContractPartner: string;
    },
  ) => {
    const response = await api.post<{
      statusCode: number;
      message: string;
      data: { request: ConsultantRequest; customer: Customer };
    }>(`/admin/consultant-requests/${requestId}/convert`, contractData);
    return response.data;
  },

  // Customer Energy Contract Update
  updateCustomerEnergyContract: async (
    customerId: string,
    contractData: {
      energyContractNumber: string;
      energyContractPartner: string;
      energyContractVerified: boolean;
    },
  ) => {
    const response = await api.put<{
      statusCode: number;
      message: string;
      data: Customer;
    }>(`/admin/customers/${customerId}/energy-contract`, contractData);
    return response.data;
  },

  // Customer Contact Update
  updateCustomerContact: async (
    customerId: string,
    contactData: {
      email?: string;
      phonePrefix?: string;
      phoneNumber?: string;
    },
  ) => {
    const response = await api.patch<{
      statusCode: number;
      message: string;
      data: Customer;
    }>(`/admin/customers/${customerId}`, contactData);
    return response.data;
  },

  // Customer Password Change (Admin Override)
  changeCustomerPassword: async (customerId: string, newPassword: string) => {
    const response = await api.patch<{
      statusCode: number;
      message: string;
    }>(`/admin/customers/${customerId}/password`, { newPassword });
    return response.data;
  },

  // Dealer Contact Update
  updateDealerContact: async (
    dealerId: string,
    contactData: {
      email?: string;
      phonePrefix?: string;
      phoneNumber?: string;
    },
  ) => {
    const response = await api.patch<{
      statusCode: number;
      message: string;
      data: Dealer;
    }>(`/admin/dealers/${dealerId}`, contactData);
    return response.data;
  },

  // Dealer ZipCode Update
  updateDealerZipCode: async (dealerId: string, zipCode: string) => {
    const response = await api.patch<{ success: boolean }>(
      `/admin/dealers/${dealerId}/zipcode`,
      { zipCode },
    );
    return response.data;
  },

  // Store ZipCode Update
  updateStoreZipCode: async (
    dealerId: string,
    storeId: string,
    zipCode: string,
  ) => {
    const response = await api.patch<{ success: boolean }>(
      `/admin/dealers/${dealerId}/stores/${storeId}/zipcode`,
      { zipCode },
    );
    return response.data;
  },

  // Dealer Country Update
  updateDealerCountry: async (dealerId: string, country: string) => {
    const response = await api.patch<{ success: boolean }>(
      `/admin/dealers/${dealerId}/country`,
      { country },
    );
    return response.data;
  },

  // Store Country Update
  updateStoreCountry: async (
    dealerId: string,
    storeId: string,
    country: string,
  ) => {
    const response = await api.patch<{ success: boolean }>(
      `/admin/dealers/${dealerId}/stores/${storeId}/country`,
      { country },
    );
    return response.data;
  },

  // Customer Country Update
  updateCustomerCountry: async (customerId: string, country: string) => {
    const response = await api.patch<{ success: boolean }>(
      `/admin/customers/${customerId}/country`,
      { country },
    );
    return response.data;
  },

  // Customer ZipCode Update
  updateCustomerZipCode: async (customerId: string, zipCode: string) => {
    const response = await api.patch<{ success: boolean }>(
      `/admin/customers/${customerId}/zipcode`,
      { zipCode },
    );
    return response.data;
  },

  // Dealer Password Change (Admin Override)
  changeDealerPassword: async (dealerId: string, newPassword: string) => {
    const response = await api.patch<{
      statusCode: number;
      message: string;
    }>(`/admin/dealers/${dealerId}/password`, { newPassword });
    return response.data;
  },

  // Store Status Update (Admin)
  updateStoreStatus: async (
    dealerId: string,
    storeId: string,
    status: "active" | "pending" | "inactive",
  ) => {
    const response = await api.patch<{
      success: boolean;
      message: string;
    }>(`/admin/dealers/${dealerId}/stores/${storeId}/status`, { status });
    return response.data;
  },

  updateStoreImages: async (
    dealerId: string,
    storeId: string,
    images: { coverImage?: string; logoImage?: string },
  ) => {
    const response = await api.patch<{
      success: boolean;
      message: string;
    }>(`/admin/dealers/${dealerId}/stores/${storeId}/images`, images);
    return response.data;
  },

  updateStoreCategory: async (
    dealerId: string,
    storeId: string,
    data: { storeCategory?: string; storeSubcategory?: string },
  ) => {
    const response = await api.patch<{
      success: boolean;
      message: string;
    }>(`/admin/dealers/${dealerId}/stores/${storeId}/category`, data);
    return response.data;
  },

  // Activate Dealer by Admin (pending → activated)
  // Uses bpeApi because activation logic (prepaid topup, store activation) is in bpe-be
  activateDealerByAdmin: async (
    dealerId: string,
    data: { addPrepaidTopup?: boolean },
  ) => {
    const response = await bpeApi.post<{
      success: boolean;
      message: string;
    }>(`/admin/dealers/${dealerId}/activate`, data);
    return response.data;
  },

  // Amazon OCR Claims (via bpe-be)
  getAmazonClaims: async (params?: AmazonClaimFilters) => {
    const response = await bpeApi.get<{
      data: {
        success: boolean;
        claims: AmazonClaim[];
        total: number;
        filters: { skip: number; limit: number };
      };
    }>("/admin/amazon-ocr/claims", { params });
    return response.data.data;
  },

  getAmazonClaimStats: async () => {
    const response = await bpeApi.get<{
      data: {
        success: boolean;
        stats: AmazonClaimStats;
      };
    }>("/admin/amazon-ocr/stats");
    return response.data.data.stats;
  },

  getAmazonClaim: async (claimId: string) => {
    const response = await bpeApi.get<{
      data: {
        success: boolean;
        claim: AmazonClaim;
      };
    }>(`/admin/amazon-ocr/claims/${claimId}`);
    return response.data.data.claim;
  },

  reviewAmazonClaim: async (
    claimId: string,
    data: {
      action: "approved" | "rejected";
      approvedAmount?: number;
      approvedCurrency?: string;
      rejectionReason?: string;
      adminNote?: string;
    },
  ) => {
    const response = await bpeApi.patch<{
      data: {
        success: boolean;
        claim: AmazonClaim;
      };
    }>(`/admin/amazon-ocr/claims/${claimId}/review`, data);
    return response.data.data;
  },

  bulkReviewAmazonClaims: async (data: {
    claimIds: string[];
    action: "approved" | "rejected";
    rejectionReason?: string;
    adminNote?: string;
  }) => {
    const response = await bpeApi.post<{
      data: {
        success: boolean;
        processed: number;
        failed: number;
        results: Array<{ claimId: string; success: boolean; error?: string }>;
      };
    }>("/admin/amazon-ocr/claims/bulk-review", data);
    return response.data.data;
  },

  updateOcrAmount: async (claimId: string, amount: number) => {
    const response = await bpeApi.patch<{
      data: {
        success: boolean;
        claim: AmazonClaim;
      };
    }>(`/admin/amazon-ocr/claims/${claimId}/ocr-amount`, { amount });
    if (response.data && response.data.data) {
      return response.data.data.claim;
    }
    // @ts-ignore
    return response.data.claim;
  },

  markAmazonClaimPaid: async (claimId: string, payoutId: string) => {
    const response = await bpeApi.patch<{
      data: {
        success: boolean;
        claim: AmazonClaim;
      };
    }>(`/admin/amazon-ocr/claims/${claimId}/mark-paid`, { payoutId });
    return response.data.data;
  },

  getAmazonConfig: async () => {
    const response = await bpeApi.get<{
      data: {
        success: boolean;
        config: AmazonConfig;
      };
    }>("/admin/amazon-ocr/config");
    return response.data.data.config;
  },

  updateAmazonConfig: async (cashbackPercent: number) => {
    const response = await bpeApi.patch<{
      data: {
        success: boolean;
        config: AmazonConfig;
      };
    }>("/admin/amazon-ocr/config", { cashbackPercent });
    return response.data.data.config;
  },

  // Energy Bill Country Configuration (proxied through affiliate-manager-be)
  getAllEnergyBillConfigs: async () => {
    const response = await api.get<{
      statusCode: number;
      data: EnergyBillConfig[];
    }>("/admin/energy-savings/country-config");
    return response.data.data;
  },

  setEnergyBillCountryConfig: async (
    country: string,
    enabled: boolean,
    countryName?: string,
  ) => {
    const response = await api.put<{
      statusCode: number;
      data: EnergyBillConfig;
    }>(`/admin/energy-savings/country-config/${country.toUpperCase()}`, {
      enabled,
      countryName,
    });
    return response.data.data;
  },

  bulkImportSingleAidaForm: async (data: {
    email: string;
    aidaFormId: string;
    screenshots: string[]; // base64
    cashbackRate?: number;
  }) => {
    const response = await bpeApi.post(
      "/admin/amazon-ocr/bulk-import/process-single",
      data,
    );
    return response.data;
  },

  checkCustomersBatch: async (
    emails: string[],
  ): Promise<
    Record<
      string,
      {
        id: string | null;
        displayId: string | null;
        exists: boolean;
        type?: string;
      }
    >
  > => {
    const response = await bpeApi.post<any>(
      "/admin/amazon-ocr/bulk-import/check-customers",
      { emails },
    );

    // Handle globally wrapped responses (e.g. { data: {...}, statusCode: 200 })
    if (response.data && response.data.data) {
      return response.data.data;
    }

    return response.data;
  },

  triggerAutoApprove: async (): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await bpeApi.post<{
      success: boolean;
      message: string;
    }>("/admin/amazon-ocr/trigger-auto-approve");
    return response.data;
  },

  // Backoffice UUID Backfill

  backfillPreview: async (csvFile: File): Promise<BackfillPreviewResult> => {
    const formData = new FormData();
    formData.append("csv", csvFile);
    const response = await bpeApi.post<{
      statusCode: number;
      data: BackfillPreviewResult;
    }>("/admin/backfill/backoffice-uuid/preview", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.data;
  },

  backfillExecute: async (csvFile: File): Promise<BackfillExecuteResult> => {
    const formData = new FormData();
    formData.append("csv", csvFile);
    const response = await bpeApi.post<{
      statusCode: number;
      data: BackfillExecuteResult;
    }>("/admin/backfill/backoffice-uuid/execute", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.data;
  },

  // Backoffice Sync (Step 2)

  backofficeSyncPreview: async (
    csvFile: File,
  ): Promise<BackofficeSyncPreviewResult> => {
    const formData = new FormData();
    formData.append("csv", csvFile);
    const response = await bpeApi.post<{
      statusCode: number;
      data: BackofficeSyncPreviewResult;
    }>("/admin/backfill/backoffice-sync/preview", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.data;
  },

  backofficeSyncExecute: async (): Promise<BackofficeSyncExecuteResult> => {
    const response = await bpeApi.post<{
      statusCode: number;
      data: BackofficeSyncExecuteResult;
    }>("/admin/backfill/backoffice-sync/execute");
    return response.data.data;
  },

  dealerGoalEmailPreview: async (): Promise<DealerGoalBackfillResult> => {
    const response = await bpeApi.post<{
      statusCode: number;
      data: DealerGoalBackfillResult;
    }>("/admin/backfill/dealer-goal-email/preview");
    return response.data.data;
  },

  dealerGoalEmailExecute: async (): Promise<DealerGoalBackfillResult> => {
    const response = await bpeApi.post<{
      statusCode: number;
      data: DealerGoalBackfillResult;
    }>("/admin/backfill/dealer-goal-email/execute");
    return response.data.data;
  },

  backfillUuidSetOne: async (
    type: "customer" | "dealer",
    id: string,
    backofficeUUID: string,
  ): Promise<{ ok: boolean }> => {
    const response = await bpeApi.post<{
      statusCode: number;
      data: { ok: boolean };
    }>("/admin/backfill/backoffice-uuid/execute-one", {
      type,
      id,
      backofficeUUID,
    });
    return response.data.data;
  },

  backofficeSyncExecuteOne: async (
    type: "customer" | "dealer",
    id: string,
  ): Promise<{ ok: boolean }> => {
    const response = await bpeApi.post<{
      statusCode: number;
      data: { ok: boolean };
    }>("/admin/backfill/backoffice-sync/execute-one", { type, id });
    return response.data.data;
  },

  // Missing Records Recovery

  missingRecordsPreview: async (
    csvFile: File,
  ): Promise<MissingRecordsPreviewResult> => {
    const formData = new FormData();
    formData.append("csv", csvFile);
    const response = await bpeApi.post<{
      statusCode: number;
      data: MissingRecordsPreviewResult;
    }>("/admin/backfill/missing-records/preview", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.data;
  },

  missingRecordsExecute: async (
    csvFile: File,
  ): Promise<MissingRecordsExecuteResult> => {
    const formData = new FormData();
    formData.append("csv", csvFile);
    const response = await bpeApi.post<{
      statusCode: number;
      data: MissingRecordsExecuteResult;
    }>("/admin/backfill/missing-records/execute", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.data;
  },

  missingRecordsCreateOne: async (data: {
    type: "customer" | "dealer";
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    phone?: string;
    street?: string;
    zipCode?: string;
    city?: string;
    country?: string;
    sponsorId?: string;
    backofficeUUID?: string;
  }): Promise<{ ok: boolean; id: string; email: string }> => {
    const response = await bpeApi.post<{
      statusCode: number;
      data: { ok: boolean; id: string; email: string };
    }>("/admin/backfill/missing-records/create-one", data);
    return response.data.data;
  },

  // Sponsor Updates

  updateCustomerSponsor: async (
    customerId: string,
    data: { sponsorId: string; sponsorType: "dealer" | "sales_rep" | "office" },
  ): Promise<{
    success: boolean;
    message: string;
    data: { sponsorId: string; sponsorType: string };
  }> => {
    const response = await bpeApi.patch(
      `/admin/customers/${customerId}/sponsor`,
      data,
    );
    return response.data;
  },

  updateDealerSponsor: async (
    dealerId: string,
    data: { sponsorId: string },
  ): Promise<{
    success: boolean;
    message: string;
    data: { sponsorId: string };
  }> => {
    const response = await bpeApi.patch(
      `/admin/dealers/${dealerId}/sponsor`,
      data,
    );
    return response.data;
  },
};

export default apiService;
